import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { promiseWrapper } from '@/helpers';
import { _t } from '@/locale';
import { bookServices } from '@/services/bookServicesTs';
import { changeBookingTimeResponseSchema } from '@/types/api/services/booking';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ErpTimeSlot } from './availability.types';
import { toWallClockAsUtc } from './PickTime.helpers';

export type ChangeBookingTime = {
  /**
   * The picked slot's wall-clock start relabelled as UTC, in ms, while it awaits confirmation:
   * not the slot's instant, so it reads right only when formatted in UTC.
   */
  pendingTimestamp?: number;
  isSubmitting: boolean;
  /**
   * Counts the moves the ERP refused. A refusal most often means the slot was taken since the
   * week was fetched, so the calendar refetches on each one rather than keep offering it.
   */
  failedMoveCount: number;
  onSelectSlot: (slot: ErpTimeSlot) => void;
  onConfirm: () => Promise<void>;
  onClose: () => void;
};

/**
 * Change-time's replacement for the checkout handoff: a picked slot waits for the customer to
 * confirm it, then moves the booking in place. Success lands on the booking page, whose
 * `changedBookingTime` state announces the move; a failure keeps the customer on the calendar
 * to pick again.
 */
export function useChangeBookingTime(bookingId: number | undefined): ChangeBookingTime {
  const history = useHistory();
  const [pendingSlot, setPendingSlot] = useState<ErpTimeSlot | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failedMoveCount, setFailedMoveCount] = useState(0);

  const onSelectSlot = useCallback((slot: ErpTimeSlot) => setPendingSlot(slot), []);
  // A move already sent cannot be taken back, so the dialog stays until it has answered: closing
  // it early would let a second slot be picked that the first move's outcome then overrides.
  const onClose = useCallback(() => {
    if (!isSubmitting) setPendingSlot(undefined);
  }, [isSubmitting]);

  const onConfirm = useCallback(async () => {
    if (!bookingId || !pendingSlot) return;
    setIsSubmitting(true);
    const { data, error } = await promiseWrapper(
      bookServices.changeBookingTime(bookingId, toWallClockAsUtc(pendingSlot.start).unix()),
    );
    const response = changeBookingTimeResponseSchema.safeParse(data);
    setIsSubmitting(false);

    if (error || !response.success) {
      toast(({ closeToast }) => <Snackbar type="danger" label={_t('serverError')} onClose={closeToast} />);
      setPendingSlot(undefined);
      setFailedMoveCount((count) => count + 1);
      return;
    }
    history.replace({ pathname: `/bokning/${response.data.id}`, state: { changedBookingTime: true } });
  }, [bookingId, pendingSlot, history]);

  return {
    pendingTimestamp: pendingSlot ? toWallClockAsUtc(pendingSlot.start).valueOf() : undefined,
    isSubmitting,
    failedMoveCount,
    onSelectSlot,
    onConfirm,
    onClose,
  };
}
